import { useRef, useCallback, useEffect } from "react";

type Direction = "ne" | "e" | "se" | "sw" | "w" | "nw";

interface ImageResizerProps {
  imageRef: React.RefObject<HTMLImageElement>;
  onResizeEnd: (width: number) => void;
  maxWidth: number;
}

const MIN_WIDTH = 100;

export function ImageResizer({
  imageRef,
  onResizeEnd,
  maxWidth,
}: ImageResizerProps) {
  const controlWrapperRef = useRef<HTMLDivElement>(null);
  const userSelectRef = useRef<{
    priority: string;
    value: string;
  } | null>(null);
  const positioningRef = useRef<{
    currentWidth: number;
    direction: Direction;
    isResizing: boolean;
    ratio: number;
    startWidth: number;
    startX: number;
  }>({
    currentWidth: 0,
    direction: "se",
    isResizing: false,
    ratio: 1,
    startWidth: 0,
    startX: 0,
  });

  const setStartCursor = (direction: Direction) => {
    const cursors: Record<Direction, string> = {
      ne: "nesw-resize",
      e: "ew-resize",
      se: "nwse-resize",
      sw: "nesw-resize",
      w: "ew-resize",
      nw: "nwse-resize",
    };
    document.body.style.cursor = cursors[direction];
  };

  const setEndCursor = () => {
    document.body.style.cursor = "default";
  };

  useEffect(() => {
    return () => {
      const positioning = positioningRef.current;
      if (!positioning.isResizing) return;

      positioning.isResizing = false;

      // Restore user selection if unmounted mid-resize
      if (userSelectRef.current) {
        document.body.style.setProperty(
          "user-select",
          userSelectRef.current.value,
          userSelectRef.current.priority
        );
      }

      document.body.style.cursor = "default";
    };
  }, []);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>, direction: Direction) => {
      if (!imageRef.current) return;

      event.preventDefault();

      const image = imageRef.current;
      const { width, height } = image.getBoundingClientRect();

      positioningRef.current = {
        currentWidth: width,
        direction,
        isResizing: true,
        ratio: height / width,
        startWidth: width,
        startX: event.clientX,
      };

      // Disable user selection during resize
      userSelectRef.current = {
        priority: document.body.style.getPropertyPriority("user-select"),
        value: document.body.style.getPropertyValue("user-select"),
      };
      document.body.style.setProperty("user-select", "none", "important");

      setStartCursor(direction);

      // Capture pointer to track outside the element
      (event.target as HTMLDivElement).setPointerCapture(event.pointerId);
    },
    [imageRef]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const positioning = positioningRef.current;
      if (!positioning.isResizing || !imageRef.current) return;

      const { direction, startWidth, startX } = positioning;

      // Calculate delta based on direction
      let deltaX = event.clientX - startX;

      // Invert delta for west-side handles
      if (direction === "w" || direction === "nw" || direction === "sw") {
        deltaX = -deltaX;
      }

      // Calculate new width from X delta with constraints
      const newWidth = Math.max(MIN_WIDTH, Math.min(startWidth + deltaX, maxWidth));

      // Update positioning
      positioning.currentWidth = newWidth;

      // Update image preview
      imageRef.current.style.width = `${newWidth}px`;
    },
    [imageRef, maxWidth]
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const positioning = positioningRef.current;
      if (!positioning.isResizing) return;

      positioning.isResizing = false;

      // Restore user selection
      if (userSelectRef.current) {
        document.body.style.setProperty(
          "user-select",
          userSelectRef.current.value,
          userSelectRef.current.priority
        );
      }

      setEndCursor();

      // Release pointer capture
      (event.target as HTMLDivElement).releasePointerCapture(event.pointerId);

      // Notify parent of final width
      onResizeEnd(Math.round(positioning.currentWidth));
    },
    [onResizeEnd]
  );

  const directions: Direction[] = ["ne", "e", "se", "sw", "w", "nw"];

  return (
    <div ref={controlWrapperRef}>
      {directions.map((direction) => (
        <div
          key={direction}
          className={`image-resizer image-resizer-${direction}`}
          onPointerDown={(e) => handlePointerDown(e, direction)}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        />
      ))}
    </div>
  );
}
